import { ICreateEngineFiscalRulesState } from "@/@types/engine-fiscal-rules";
import { useCreateEngineFiscalRulesMutation } from "@/services/engine-fiscal-rules";
import { zodResolver } from "@hookform/resolvers/zod";
import { Load } from "components/load";
import { Button } from "components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "components/ui/form";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { toast } from "components/ui/use-toast";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { MESSAGES } from "../../utils/message";
import {
  formatStandartRate,
  UF_OPTIONS,
  TAX_OPTIONS_CALCULATION_BASIS,
} from "../../utils/utils";
import { ConfirmRegisterButton } from "./components/confirm-dialog";
import { ModernCard } from "./components/modern-card";
import { TaxItemsCard } from "./components/tax-items-card";
import { useFiscalRulesListActions } from "./ducks/hooks/action";
import { useFiscalRulesListState } from "./ducks/hooks/state";
import { FormSchema } from "./validation-forms";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { PageLayout, PageTitle } from "components/layout";
import { SelectDate } from "@/pages/tax/exception-tax/list/create/components/select-date";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";

export const CreateEngineFiscalRulesState: React.FC = () => {
  const [createEngineFiscalRules, { isLoading }] =
    useCreateEngineFiscalRulesMutation();
  const { addFiscalRule, removeFiscalRule, clearFiscalRulesList } =
    useFiscalRulesListActions();
  const fiscalRulesList = useFiscalRulesListState();
  const navigate = useNavigate();

  useEffect(() => {
    clearFiscalRulesList();
  }, []);

  const form = useForm<ICreateEngineFiscalRulesState>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      state: "",
      tip: "",
      alq: "",
      cst: "",
      cfop: "",
      classification_tax: "",
      initial_term: "",
      final_term: "",
    },
  });

  const tipValue = form.watch("tip");
  const showClassificationTax = tipValue === "6" || tipValue === "7";
  const showCfop = tipValue === "1";

  async function onSubmit(data: ICreateEngineFiscalRulesState) {
    addFiscalRule({
      state: data.state,
      tip: parseInt(data.tip as string),
      alq: parseFloat(data.alq.toString().replace(",", ".")),
      cst: data.cst,
      cfop: data.cfop,
      classification_tax: data.classification_tax || undefined,
      initial_term: data.initial_term!,
      final_term: data.final_term || undefined,
    });

    form.reset({
      state: "",
      tip: "",
      alq: "",
      cst: "",
      cfop: "",
      classification_tax: "",
      initial_term: "",
      final_term: "",
    });

    toast(
      MESSAGES.CREATE.WARNING || {
        variant: "alert",
        description: "Regra fiscal adicionada com sucesso!",
      }
    );
  }

  async function handleRegisterAll() {
    if (fiscalRulesList.length === 0) {
      console.warn("Nenhuma regra fiscal para cadastrar.");
      return;
    }
    try {
      await createEngineFiscalRules(fiscalRulesList).unwrap();
      clearFiscalRulesList();
      form.reset();
      toast(
        MESSAGES.CREATE.SUCCESS || {
          description: "Cadastro realizado com sucesso!",
        }
      );
      navigate("/fiscal/aliquotas-base", {
        state: { shouldRefetch: true },
      });
    } catch (error) {
      console.error("Erro ao cadastrar regras fiscais:", error);
      toast(MESSAGES.CREATE.ERROR);
    }
  }

  const onDelete = (item: ICreateEngineFiscalRulesState) => {
    removeFiscalRule(item);
  };

  if (isLoading) return <Load />;

  return (
    <PageLayout>
      <div className="flex justify-start w-full">
        <ArrowLeft
          onClick={() => navigate(-1)}
          className="cursor-pointer hover:text-gray-600 transition"
        />
      </div>
      <PageTitle
        title="Cadastro de Alíquota Base"
        description="Preencha os campos abaixo para cadastrar uma nova alíquota base"
      />

      <div className="flex flex-col items-start justify-start mt-5 w-full">
        <Form {...form}>
          <form className="flex justify-center items-center flex-col w-full">
            <div className=" w-[90%] flex flex-col">
              <ModernCard
                title="Informações Básicas"
                description="Dados principais da nova alíquota"
                className="w-full"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                          UF
                        </Label>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value ?? ""}
                        >
                          <FormControl>
                            <SelectTrigger
                              hasError={!!form.formState.errors.state}
                              className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                            >
                              <SelectValue placeholder="Selecione uma UF" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {UF_OPTIONS.map((uf) => (
                              <SelectItem key={uf} value={uf}>
                                {uf}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tip"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                          Tipo de Tributo
                        </Label>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value?.toString() ?? ""}
                        >
                          <FormControl>
                            <SelectTrigger
                              hasError={!!form.formState.errors.tip}
                              className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                            >
                              <SelectValue placeholder="Selecione o Tributo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {TAX_OPTIONS_CALCULATION_BASIS.map((tax) => (
                              <SelectItem key={tax.value} value={tax.value}>
                                {tax.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="alq"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                          Alíquota
                        </Label>
                        <div className="relative w-full">
                          <Input
                            {...field}
                            placeholder="Ex: 18"
                            className="pr-8 w-full h-12"
                            value={formatStandartRate(
                              String(field.value ?? "")
                            )}
                            onChange={(e) =>
                              field.onChange(formatStandartRate(e.target.value))
                            }
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                            %
                          </span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </ModernCard>

              <ModernCard
                className="mt-5"
                title="Período de Vigência"
                description="Defina o período em que a alíquota estará ativa"
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="initial_term"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                          Data Inicial da Vigência
                        </Label>
                        <SelectDate
                          value={field.value}
                          onChange={field.onChange}
                          id={""}
                          label={""}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="final_term"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                          Data Final da Vigência
                        </Label>
                        <SelectDate
                          value={field.value}
                          onChange={field.onChange}
                          id={""}
                          label={""}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </ModernCard>

              <ModernCard
                className="mt-5"
                title="Configurações Fiscais"
                description="Parâmetros tributários e códigos fiscais"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {showClassificationTax && (
                    <FormField
                      control={form.control}
                      name="classification_tax"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <div className="flex items-center gap-2 ">
                            <TooltipProvider>
                              <div className="flex items-center gap-2  relative z-[100]">
                                <Label className="text-sm font-semibold text-gray-800">
                                  Classificação Tributária
                                </Label>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="cursor-help"
                                    >
                                      <HelpCircle className="h-4 w-4 text-gray-500 hover:text-gray-700 transition-colors" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    className="max-w-xs bg-[#F5A524] text-gray-100 font-semibold p-3 rounded-lg shadow-lg z-[9999]"
                                  >
                                    <p className="text-sm">
                                      A classificação tributária é obrigatória
                                      para os tributos IBS e CBS. Ela define
                                      como o produto será tratado para fins de
                                      tributação.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TooltipProvider>
                          </div>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Digite a classificação tributária"
                              className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {showCfop && (
                    <FormField
                      control={form.control}
                      name="cfop"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                            CFOP
                          </Label>
                          <Input
                            {...field}
                            className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                            placeholder="CFOP"
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="cst"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                          CST
                        </Label>
                        <Input
                          {...field}
                          className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                          placeholder="CST"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </ModernCard>
            </div>

            <div className="w-full flex justify-end gap-4 mt-4">
              <Button type="button" onClick={form.handleSubmit(onSubmit)}>
                Adicionar
              </Button>
            </div>
          </form>
        </Form>
      </div>

      <div className="w-full mt-6">
        <ModernCard
          title="Alíquotas Adicionadas"
          description="Lista de alíquotas adicionadas"
        >
          <TaxItemsCard items={fiscalRulesList} onRemove={onDelete} />
          <div className="flex justify-end gap-3 mt-6 w-full">
            <Button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-200 transition"
              onClick={() => navigate(-1)}
            >
              Voltar
            </Button>

            <ConfirmRegisterButton
              onConfirm={handleRegisterAll}
              isLoading={isLoading}
            >
              Confirmar
            </ConfirmRegisterButton>
          </div>
        </ModernCard>
      </div>
    </PageLayout>
  );
};
